/**
 * MCP agent access: the deployment's own settings, its credentials, and what
 * the surface has been doing.
 *
 * The created credential's secret is returned by exactly one call here and is
 * never re-readable. Nothing in this module stores it — callers must keep it
 * in component state and drop it when the operator dismisses the panel.
 */

import { fetchApi } from '../core';
import type {
  CreateMcpTokenData,
  CreatedMcpTokenResponse,
  McpActivityResponse,
  McpConsoleSettings,
  McpTokenListResponse,
  McpTokenResponse,
} from '../types';

/** Endpoint, protocol revision, grantable scopes, and the ceilings the server
 *  will clamp to. Available even when the surface is switched off. */
export function getSettings(componentName: string = 'Unknown') {
  return fetchApi<McpConsoleSettings>('/tenant/mcp-settings', {}, componentName);
}

export function listTokens(
  params: { page?: number; limit?: number } = {},
  componentName: string = 'Unknown'
) {
  const search = new URLSearchParams();
  if (typeof params.page === 'number') search.set('page', String(params.page));
  if (typeof params.limit === 'number') search.set('limit', String(params.limit));
  const qs = search.toString();
  return fetchApi<McpTokenListResponse>(
    `/tenant/mcp-tokens${qs ? `?${qs}` : ''}`,
    {},
    componentName
  );
}

/** Mint a credential. The response carries the only copy of the secret. */
export function createToken(data: CreateMcpTokenData, componentName: string = 'Unknown') {
  return fetchApi<CreatedMcpTokenResponse>(
    '/tenant/mcp-tokens',
    { method: 'POST', body: JSON.stringify(data) },
    componentName
  );
}

/** Revoke immediately. Idempotent: revoking an already-revoked credential
 *  returns it unchanged rather than failing. */
export function revokeToken(tokenId: string, componentName: string = 'Unknown') {
  if (!/^[a-f0-9]{24}$/i.test(tokenId)) {
    throw new Error('Invalid MCP credential identifier');
  }
  return fetchApi<McpTokenResponse>(
    `/tenant/mcp-tokens/${encodeURIComponent(tokenId)}`,
    { method: 'DELETE' },
    componentName
  );
}

/** Calls, refusals, and failures over a window of days (1–90, default 30). */
export function getActivity(days: number | undefined, componentName: string = 'Unknown') {
  const qs = typeof days === 'number' ? `?days=${days}` : '';
  return fetchApi<McpActivityResponse>(`/tenant/mcp-activity${qs}`, {}, componentName);
}
